import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Product } from '../Shop';
import { Helmet } from 'react-helmet-async';

interface ProductMetaTagsProps {
  product: Product;
  baseUrl?: string;
}

/**
 * Component to dynamically set meta tags for product pages
 * Enhanced for better WhatsApp and social media sharing
 */
const ProductMetaTags: React.FC<ProductMetaTagsProps> = ({ product, baseUrl = 'https://www.aiaj.xyz' }) => {
  const { language } = useLanguage();
  const currentLang = language === 'ar' ? 'ar' : 'en';

  // Get product title in the current language
  const productTitle = language === 'ar' ? product.titleAr : product.titleEn;

  // Get product description in the current language (strip HTML)
  const stripHtml = (html: string) => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };

  const productDescription = stripHtml(
    language === 'ar' ? (product.descriptionAr || '') : (product.descriptionEn || '')
  ).substring(0, 160) + '...'; // Limit to 160 chars for meta description


// In ProductMetaTags.tsx, update the getProductImage function to be more robust:
const getProductImage = () => {
  // First try to get a product image
  if (product.images && product.images.length > 0) {
    const mainImage = product.images.find(img => img.isMain) || product.images[0];
    let imageUrl = mainImage.url;

    // Ensure the URL is absolute
    if (!imageUrl.startsWith('http')) {
      // Handle different URL formats
      if (imageUrl.startsWith('/')) {
        imageUrl = `${baseUrl}${imageUrl}`;
      } else {
        imageUrl = `${baseUrl}/${imageUrl}`;
      }
    }
    return imageUrl;
  }

  // If no product image, fall back to a product-specific placeholder if available
  return `${baseUrl}/product-placeholder.jpg`; // Create this if needed
};
  const productImage = getProductImage();

  // Current product URL
  const productUrl = `${baseUrl}/shop/product/${product.id}?v=${Date.now()}`;

  // Enhanced title for social sharing
// Change the socialTitle to include both product and academy info
const socialTitle = `${productTitle} | ${language === 'ar' ? 'أكاديمية الذكاء الاصطناعي ' : 'AI Academy MENA'}`;
  const enhancedDescription = `${productDescription} | AI Academy MENA - ${language === 'ar' ? 'أكاديمية الذكاء الاصطناعي ' : 'Educational Tools'}`;

  return (
    <Helmet>
      {/* Basic Meta Tags - Use product title as main title */}
      <title>{productTitle}</title>
      <meta name="description" content={enhancedDescription} />

      {/* Dynamic Favicon - Use product image */}
      <link rel="icon" href={productImage} />
      <link rel="shortcut icon" href={productImage} />
      <link rel="apple-touch-icon" href={productImage} />

      {/* Canonical URL */}
      <link rel="canonical" href={productUrl} />

      {/* Open Graph / Facebook - Enhanced for WhatsApp */}
      <meta property="og:type" content="product" />
      <meta property="og:url" content={productUrl} />
      <meta property="og:title" content={socialTitle} />
      <meta property="og:description" content={enhancedDescription} />
      <meta property="og:image" content={productImage} />
      <meta property="og:image:secure_url" content={productImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={productTitle} />
      <meta property="og:site_name" content="AI Academy MENA" />
      <meta property="og:updated_time" content={new Date().toISOString()} />
      <meta property="og:locale" content={currentLang === 'ar' ? 'ar_AR' : 'en_US'} />

      {/* Product specific Open Graph tags */}
      <meta property="product:price:amount" content={product.price.toString()} />
      <meta property="product:price:currency" content="ILS" />
      <meta property="product:availability" content="in stock" />
      <meta property="product:condition" content="new" />
      <meta property="product:retailer_item_id" content={product.id.toString()} />

      {/* Twitter Card - Enhanced */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={productUrl} />
      <meta name="twitter:title" content={socialTitle} />
      <meta name="twitter:description" content={enhancedDescription} />
      <meta name="twitter:image" content={productImage} />
      <meta name="twitter:image:alt" content={productTitle} />
      <meta name="twitter:site" content="@ai_academy_jerusalem" />

      {/* WhatsApp specific meta tags */}
      <meta property="og:image:type" content="image/jpeg" />
      <meta name="thumbnail" content={productImage} />

      {/* Additional meta tags for better link preview */}
      <meta name="author" content="AI Academy MENA" />
      <meta name="robots" content="index, follow" />
      <meta name="revisit-after" content="7 days" />

      {/* Structured Data / JSON-LD - Enhanced */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org/',
          '@type': 'Product',
          '@id': productUrl,
          name: productTitle,
          description: enhancedDescription,
          image: [productImage],
          url: productUrl,
          sku: product.id.toString(),
          category: product.category,
          offers: {
            '@type': 'Offer',
            '@id': `${productUrl}#offer`,
            url: productUrl,
            priceCurrency: 'ILS',
            price: product.price,
            availability: 'https://schema.org/InStock',
            priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1 year from now
            itemCondition: 'https://schema.org/NewCondition',
            seller: {
              '@type': 'Organization',
              '@id': `${baseUrl}#organization`,
              name: currentLang === 'ar' ? 'أكاديمية الذكاء الاصطناعي ' : 'AI Academy MENA',
              url: baseUrl,
              logo: `${baseUrl}/favicon.jpg`,
              sameAs: [
                'https://www.facebook.com/profile.php?id=61564188212828',
                'https://www.linkedin.com/company/ai-academy-jerusalem',
                'https://www.instagram.com/academy.ai45/'
              ]
            }
          },
          brand: {
            '@type': 'Brand',
            '@id': `${baseUrl}#brand`,
            name: currentLang === 'ar' ? 'أكاديمية الذكاء الاصطناعي' : 'AI Academy MENA',
            logo: `${baseUrl}/favicon.jpg`
          },
          manufacturer: {
            '@type': 'Organization',
            name: currentLang === 'ar' ? 'أكاديمية الذكاء الاصطناعي ' : 'AI Academy MENA'
          }
        })}
      </script>

      {/* Additional JSON-LD for better SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: baseUrl
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Shop',
              item: `${baseUrl}/shop`
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: productTitle,
              item: productUrl
            }
          ]
        })}
      </script>
    </Helmet>
  );
};

export default ProductMetaTags;